"use client";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  analyticsNoticeSeen,
  CONSENT_EVENT,
  dismissAnalyticsNotice,
  synchronizeAnalyticsConsent,
} from "./consent";
import { publicPage } from "./policy";

export default function CookieConsent() {
  const path = usePathname();
  const [visible, setVisible] = useState(false);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(true);
    const refresh = () => {
      synchronizeAnalyticsConsent();
      setVisible(!analyticsNoticeSeen());
    };
    const manage = () => setVisible(true);
    refresh();
    window.addEventListener(CONSENT_EVENT, refresh);
    window.addEventListener("storage", refresh);
    window.addEventListener("farbin:privacy-preferences", manage);
    return () => {
      window.removeEventListener(CONSENT_EVENT, refresh);
      window.removeEventListener("storage", refresh);
      window.removeEventListener("farbin:privacy-preferences", manage);
    };
  }, []);
  if (!ready) return null;
  return (
    <ConsentBanner
      open={Boolean(visible && path && publicPage(path))}
      onDecision={dismissAnalyticsNotice}
    />
  );
}

export function ConsentBanner({
  open,
  onDecision,
}: {
  open: boolean;
  onDecision: (accepted: boolean) => void;
}) {
  const reduced = useReducedMotion();
  return (
    <AnimatePresence>
      {open ? (
        <motion.section
          key="analytics-consent"
          dir="ltr"
          role="region"
          aria-label="Cookie notice"
          aria-describedby="analytics-cookie-description"
          className="text-slateCustom border-slateCustom/15 fixed inset-x-0 bottom-0 z-[11001] flex max-h-[60dvh] flex-col items-start gap-4 overflow-y-auto border-t bg-white px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[0_-4px_24px_#00000010] sm:flex-row sm:items-center sm:justify-between sm:px-7"
          initial={{
            opacity: 0,
            y: reduced ? 0 : 16,
          }}
          animate={{ opacity: 1, y: 0 }}
          exit={{
            opacity: 0,
            y: reduced ? 0 : 8,
          }}
          transition={{
            duration: reduced ? 0 : 0.24,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          <p
            id="analytics-cookie-description"
            className="text-slateCustom/75 text-sm leading-6"
          >
            Farbin uses cookies to deliver and enhance the quality of its
            services and to analyze traffic.
          </p>
          <button
            type="button"
            onClick={() => onDecision(true)}
            className="bg-pinkCustom focus-visible:outline-slateCustom min-h-11 shrink-0 rounded-full px-6 py-3 text-sm font-semibold text-white focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            OK, got it
          </button>
        </motion.section>
      ) : null}
    </AnimatePresence>
  );
}
export function PrivacyPreferencesButton() {
  return (
    <button
      type="button"
      onClick={() =>
        window.dispatchEvent(new Event("farbin:privacy-preferences"))
      }
      className="focus-visible:outline-pinkCustom min-h-11 rounded px-2 py-2 text-xs underline underline-offset-4 focus-visible:outline-2"
    >
      Cookie information
    </button>
  );
}
